import { memoize } from "@use/decorators";

import { ElementModel, ElementModelData } from "@courses-pm2/api/src/ts/models/ElementModel";
import { reduceRichTextNodeStandardPageCount, renderContentNodes, RichtextContentNode } from "@courses-pm2/api/src/ts/models/elements/RichtextModel";

/**
 * Specification of data needed to instantiate a new GlossaryWordModel.
 */
type GridData = {
    [K in "id" | "content"]: GlossaryWordModel[K];
};

/**
 * Describes the content to be rendered for the element, so that it's made
 * easily available.
 */
export class GlossaryWordModel extends ElementModel {
    /**
     * Specifies the id of the glossary word, so that it's definition can be
     * fetched at a later time.
     */
    public readonly id: string;

    /**
     * Contains the content that should be rendered within the glossary word
     * trigger node.
     */
    public readonly content: RichtextContentNode[];

    /**
     * Apply data given when a new model is being instantiated.
     */
    constructor(initial: GridData & ElementModelData) {
        super(initial);

        this.id = initial.id;
        this.content = initial.content;
    }

    /**
     * Helper that'll transform the content of the GlossaryWord into React nodes
     * that can be mounted directly.
     */
    @memoize()
    public render(): React.ReactNode[] {
        if (!this.engine) {
            throw new Error("GlossaryWordModel.render(): No rendering engine provided to this model.");
        }

        return renderContentNodes(this.content, this.engine);
    }

    /**
     * Preview is unavailable for GlossaryWords.
     */
    public async getPreviewImageUrl(): Promise<undefined> {
        return undefined;
    }

    /**
     * Preview is unavailable for GlossaryWords.
     */
    public getPreviewText(): string | undefined {
        return undefined;
    }

    /**
     * @inheritdoc
     *
     * Element links are able to contain RichTextNodes, so use the helper from
     * the RichtextModel to calculate standard pages for the content.
     */
    // tslint:disable-next-line: completed-docs
    @memoize()
    public getStandardPageCount(): number {
        return reduceRichTextNodeStandardPageCount(this.content);
    }
}
